import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useLocation, Navigate, Outlet } from 'react-router-dom';
import { AUTH_EXPIRED_EVENT } from '../utils/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Types matching backend AdminUserPublic
interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'site_admin' | 'org_admin';
  mfaEnabled: boolean;
  isActive: boolean;
  orgAccess: Array<{ orgId: string; orgRole: 'org_full_admin' | 'org_viewer' }>;
  createdAt: string;
  updatedAt: string;
}

type AuthState =
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'needs_mfa_setup'
  | 'needs_mfa_verify';

interface AuthContextType {
  user: AdminUser | null;
  authState: AuthState;
  mfaToken: string | null; // For MFA verify flow
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyMfa: (totpCode: string) => Promise<void>;
  setupMfa: () => Promise<{ qrCodeDataUrl: string; secret: string }>;
  confirmMfa: (totpCode: string) => Promise<void>;
  isSiteAdmin: boolean;
  hasOrgAccess: (orgId: string) => boolean;
  getOrgRole: (orgId: string) => 'org_full_admin' | 'org_viewer' | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function authFetch(endpoint: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  return fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Reset auth state when the API client detects a 401 (session expired)
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      setMfaToken(null);
      setAuthState('unauthenticated');
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const checkAuth = async () => {
    try {
      const res = await authFetch('/api/admin/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.data);
        setAuthState('authenticated');
      } else {
        setUser(null);
        setAuthState('unauthenticated');
      }
    } catch {
      setUser(null);
      setAuthState('unauthenticated');
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    const res = await authFetch('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }

    if (data.data.mfaRequired) {
      setMfaToken(data.data.mfaToken);
      setUser(data.data.user);
      setAuthState('needs_mfa_verify');
      return;
    }

    if (data.data.mfaSetupRequired) {
      setUser(data.data.user);
      setAuthState('needs_mfa_setup');
      return;
    }

    // Direct auth (shouldn't happen with mandatory MFA, but handle it)
    setUser(data.data.user);
    setAuthState('authenticated');
  }, []);

  const verifyMfa = useCallback(
    async (totpCode: string) => {
      if (!mfaToken) throw new Error('No MFA token');

      const res = await authFetch('/api/admin/auth/verify-mfa', {
        method: 'POST',
        body: JSON.stringify({ mfaToken, totpCode }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'MFA verification failed');
      }

      setUser(data.data.user);
      setMfaToken(null);
      setAuthState('authenticated');
    },
    [mfaToken]
  );

  const setupMfa = useCallback(async (): Promise<{ qrCodeDataUrl: string; secret: string }> => {
    const res = await authFetch('/api/admin/auth/mfa/setup', { method: 'POST' });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'MFA setup failed');
    }

    return data.data;
  }, []);

  const confirmMfa = useCallback(async (totpCode: string) => {
    const res = await authFetch('/api/admin/auth/mfa/confirm', {
      method: 'POST',
      body: JSON.stringify({ totpCode }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'MFA confirmation failed');
    }

    setUser(data.data.user);
    setAuthState('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await authFetch('/api/admin/auth/logout', { method: 'POST' });
    } catch {
      // Logout even on error
    }
    setUser(null);
    setMfaToken(null);
    setAuthState('unauthenticated');
  }, []);

  const isSiteAdmin = user?.role === 'site_admin';

  const hasOrgAccess = useCallback(
    (orgId: string) => {
      if (!user) return false;
      if (user.role === 'site_admin') return true;
      return user.orgAccess.some((a) => a.orgId === orgId);
    },
    [user]
  );

  const getOrgRole = useCallback(
    (orgId: string): 'org_full_admin' | 'org_viewer' | null => {
      if (!user) return null;
      if (user.role === 'site_admin') return 'org_full_admin';
      const access = user.orgAccess.find((a) => a.orgId === orgId);
      return access?.orgRole || null;
    },
    [user]
  );

  const value: AuthContextType = {
    user,
    authState,
    mfaToken,
    login,
    logout,
    verifyMfa,
    setupMfa,
    confirmMfa,
    isSiteAdmin,
    hasOrgAccess,
    getOrgRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Wrapper for protected routes. Redirects to login if not authenticated.
 * Renders an Outlet for nested routes.
 */
export function ProtectedRoute() {
  const { authState } = useAuth();
  const location = useLocation();

  if (authState === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-base-content/70">Loading...</div>
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (authState === 'needs_mfa_setup') {
    return <Navigate to="/mfa-setup" replace />;
  }

  if (authState === 'needs_mfa_verify') {
    return <Navigate to="/mfa-verify" replace />;
  }

  return <Outlet />;
}

/**
 * Wrapper that hides children for non-site_admin users.
 */
export function RequireRole({ role, children }: { role: 'site_admin'; children: ReactNode }) {
  const { user } = useAuth();
  if (!user || user.role !== role) return null;
  return <>{children}</>;
}
